import {App} from "@vue/runtime-core";
import {PeppermillCalendarChangeEventDetail} from 'peppermill-vue/src/components/PeppermillCalendar.vue'
import {isPromptForRefreshDisabled, setPromptForRefreshDisabled, getDateFromResNetInput} from "./calendar-helpers";

declare global {
  interface Window {
    pepDateFunctions: {
      language: {
        en: {
          days: string[];
          daysShort: string[];
          daysMin: string[];
          months: string[];
          monthsShort: string[];
          today: string;
          clear: string;
          dateFormat: string;
          timeFormat: string;
          firstDay: Number;
        }
      }
      formatDateObjectYYYYMMDD: (dateObject: Date) => string;
      daysBetween: (startDate: Date, endDate: Date) => number;
      addDays: (dateObject: Date, days: number) => Date;
    };
    calendarApp?: App;
    promptUserToRefreshRates: (timerDelay?: number, message?: string) => void;
    // @ts-ignore
    resnetSaveReservationSelections: (checkInDate: Date, checkOutDate: Date, overrides?: {
      checkin?: Date,
      checkout?: Date
    }) => void;
    showOverlayDialog: (linkData: OverlayDialogOptions, linkEle?: HTMLElement) => void;
    isResNetStep1?: boolean;
  }
}

let currentCheckInDate: Date | null = null;
let currentCheckOutDate: Date | null = null;
window.resnetSaveReservationSelections = function (checkInDate, checkOutDate, overrides) {
  if (typeof overrides !== 'object') overrides = {};

  if (overrides.checkin) checkInDate = overrides.checkin;
  if (overrides.checkout) checkOutDate = overrides.checkout;

  currentCheckInDate = checkInDate;
  currentCheckOutDate = checkOutDate;

  let resNetDay = '' + checkInDate.getDate();
  const resNetMonthYear = '' + checkInDate.getFullYear() + ' ' + window.pepDateFunctions.language['en'].months[checkInDate.getMonth()];
  let resNetNights = '' + parseInt(pepDateFunctions.daysBetween(checkInDate, checkOutDate), 10);

  if (resNetDay.length === 1) resNetDay = '0' + resNetDay;
  if (resNetNights.length === 1) resNetNights = '0' + resNetNights;

  const checkInDayNode: HTMLSelectElement | null = document.querySelector('select[name=LRESDDX]');
  if (checkInDayNode) {
    checkInDayNode.value = resNetDay;
  }

  const checkInMonthYearNode: HTMLSelectElement | null = document.querySelector('select[name=LRESYRMTH]');
  if (checkInMonthYearNode) {
    checkInMonthYearNode.value = resNetMonthYear;
  }

  const stayLengthNode: HTMLSelectElement | null = document.querySelector('select[name=LRESNNC]');
  if (stayLengthNode) {
    stayLengthNode.value = resNetNights;
  }
};

let refreshPromptTimer = -1;
let windowLoadFired = false;
let refreshPromptHash = createRateRefreshHash();
window.addEventListener('load', () => {
  windowLoadFired = true
});

function createRateRefreshHash() {
  const localCheckInDate = (currentCheckInDate)
    ? window.pepDateFunctions.formatDateObjectYYYYMMDD(currentCheckInDate)
    : getDateFromResNetInput((document.querySelector('input[name="PSDW1GM   "]') as HTMLInputElement | null)?.value || '');
  const localCheckOutDate = (currentCheckOutDate)
    ? window.pepDateFunctions.formatDateObjectYYYYMMDD(currentCheckOutDate)
    : getDateFromResNetInput((document.querySelector('input[name="PEDW1GM   "]') as HTMLInputElement | null)?.value || '');

  if (localCheckInDate && localCheckOutDate) {
    return localCheckInDate
      + '-'
      + localCheckOutDate
      + '-'
      + (document.querySelector('select[name=LRESNAC]') as HTMLSelectElement | null)?.value || ''
      + '-'
      + (document.querySelector('select[name=LRESNCC]') as HTMLSelectElement | null)?.value || ''
      + '-'
      + (document.querySelector('input[name=ATANOSC]') as HTMLInputElement | null)?.value || ''
      + '-'
      + (document.querySelector('input[name="ACAS#SC"]') as HTMLInputElement | null)?.value || ''
      + '-'
      + (document.querySelector('input[name=AREF1SC]') as HTMLInputElement | null)?.value || '';
  }

  return '';
}

window.promptUserToRefreshRates = function (timerDelay, message) {
  //only show prompt once the page has loaded
  if (false === windowLoadFired) return;

  if (typeof timerDelay !== 'number') {
    timerDelay = 2500;
  }
  if (typeof message !== 'string') {
    message = 'You have updated your stay details. To continue you need to refresh rates.';
  }

  clearTimeout(refreshPromptTimer);

  if (
    refreshPromptHash !== ''
    && refreshPromptHash !== createRateRefreshHash()
  ) {
    refreshPromptTimer = setTimeout(function () {
      window.showOverlayDialog({
        confirmAccept: 'REFRESH',
        confirmDeny: 'CANCEL',
        confirmTitle: 'Rate Refresh Required',
        confirmBody: message,
        callback: 'UPDATE-STAY-DETAILS'
      });

    }, timerDelay);
  }

}

export interface OverlayDialogOptions {
  alertMode?: boolean;
  confirmAccept?: string;
  confirmDeny?: string;
  confirmTitle?: string;
  confirmBody?: string;
  callback?: string;
  callbackDeny?: string;
  query?: string;
  forceCallback?: boolean;
  forceCallbackTimeout?: number;
  forceCallbackTimer?: number | null;
}

function getFancyDateFormat(date: Date | string): string {
  if ('string' === typeof date) {
    date = new Date(date);
  }
  return window.pepDateFunctions.language.en.daysShort[date.getDay()]
    + ', ' + window.pepDateFunctions.language.en.monthsShort[date.getMonth()]
    + ' ' + date.getDate();
}

const calendarWidgetNode: HTMLElement | null = document.querySelector('#calendar-widget');
const pageLinkFilterNode: HTMLElement | null = document.querySelector('.page-link-filter');
if (calendarWidgetNode && pageLinkFilterNode) {
  setPromptForRefreshDisabled(false);

  calendarWidgetNode.addEventListener('click', (event: MouseEvent) => {
    event.stopPropagation();
  });
  (document.querySelector('body') as HTMLElement).addEventListener('click', () => {
    calendarWidgetNode.classList.remove('calendar_widget-open');
  });

  let isUserEngagedWithCalendarWidget = false;
  calendarWidgetNode.addEventListener('change', (event: Event) => {
    if ('detail' in event && 'string' === typeof event.detail) {
      const detailJson = JSON.parse(event.detail) as PeppermillCalendarChangeEventDetail | null;
      if (detailJson) {
        if (!detailJson.userSelected && isUserEngagedWithCalendarWidget) {
          //need to let the user know that dates have been updated
          if ('showOverlayDialog' in window) {
            window.showOverlayDialog({
              alertMode: true,
              confirmTitle: 'Dates Updated',
              confirmBody: 'Your stay dates have been changed due to having selected unavailable dates. Please call 800-648-5555 if you would like assistance.',
            });
          } else {
            alert('Your stay dates have been changed due to having selected unavailable dates. Please call 800-648-5555 if you would like assistance.');
          }
        } else if (detailJson.userSelected) {
          isUserEngagedWithCalendarWidget = true;
        }

        if (checkInWidgetNode) {
          let newCheckInValue = '';
          if (detailJson.start) {
            newCheckInValue = getFancyDateFormat(detailJson.start || '');
          }
          (checkInWidgetNode.querySelector('input[name="check_in"]') as HTMLInputElement).value = newCheckInValue;
        }
        if (checkOutWidgetNode) {
          let newCheckOutValue = '';
          if (detailJson.end) {
            newCheckOutValue = getFancyDateFormat(detailJson.end || '');
          }
          (checkOutWidgetNode.querySelector('input[name="check_out"]') as HTMLInputElement).value = newCheckOutValue;
        }

        window.resnetSaveReservationSelections(new Date(detailJson.start), new Date(detailJson.end));

        if (detailJson.start && detailJson.end) {
          if (/*$pageLinkFilterNode.data('initialized') && */!isPromptForRefreshDisabled()) {
            //only display this prompt if we are on the FIRST step of ResNet
            if ('isResNetStep1' in window && !window.isResNetStep1) {
              window.promptUserToRefreshRates(16);
            }
          }

          // $pageLinkFilterNode.data('initialized', true);
          // $pageLinkFilterNode.data('checkInDate', new Date(detailJson.start));
          // $pageLinkFilterNode.data('checkOutDate', new Date(detailJson.end));
        }
      }
    }
  });

  calendarWidgetNode.addEventListener('error', (event: Event) => {
    if ('detail' in event && 'no-dates-available' === event.detail) {
      //stop propagation for jQuery's sake
      event.stopPropagation();
      document.querySelectorAll('.book-room').forEach((bookButtonNode) => {
        (bookButtonNode as HTMLElement).style.display = 'none';
      });
      if ('showOverlayDialog' in window) {
        window.showOverlayDialog({
          alertMode: true,
          confirmTitle: 'No Available Dates',
          confirmBody: 'You have existing reservation(s) and no dates are available. Please call 800-648-5555 for assistance.',
        });
      } else {
        alert('You have existing reservation(s) and no dates are available. Please call 800-648-5555 for assistance.');
      }
    }
  });

  const checkInWidgetNode: HTMLElement | null = document.querySelector('.check-in');
  const checkOutWidgetNode: HTMLElement | null = document.querySelector('.check-out');
  if (checkInWidgetNode) {
    checkInWidgetNode.addEventListener('click', (event: MouseEvent) => {
      event.stopPropagation();
      calendarWidgetNode.classList.add('calendar_widget-open');
    });
  }
  if (checkOutWidgetNode) {
    checkOutWidgetNode.addEventListener('click', (event: MouseEvent) => {
      event.stopPropagation();
      calendarWidgetNode.classList.add('calendar_widget-open');
    });
  }
}